/**
 * ProjectionsList Component
 * Grid/list view of box office projections with filtering and sorting
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Chip,
  Alert
} from '@mui/material';
import {
  Search,
  Sort,
  FilterList
} from '@mui/icons-material';
import ProjectionCard from './ProjectionCard';

const ProjectionsList = ({ 
  projections, 
  filmId, 
  filmTitle, 
  canManage = false,
  onRefresh,
  onEditProjection,
  loading = false,
  error = null 
}) => {
  const [sortBy, setSortBy] = useState('projectionDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');

  // Sort and filter projections
  const filteredAndSortedProjections = useMemo(() => {
    if (!projections || projections.length === 0) return [];

    let filtered = [...projections];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(projection => 
        projection.createdBy?.toLowerCase().includes(term) ||
        projection.notes?.toLowerCase().includes(term) ||
        projection.projectionDate?.toLowerCase().includes(term)
      );
    }

    // Apply category filter
    if (filterBy !== 'all') {
      const now = new Date();
      filtered = filtered.filter(projection => {
        const projectionDate = new Date(projection.projectionDate);
        const daysDiff = Math.ceil((now - projectionDate) / (1000 * 60 * 60 * 24));
        
        switch (filterBy) {
          case 'recent':
            return daysDiff <= 7;
          case 'month':
            return daysDiff <= 30;
          case 'quarter':
            return daysDiff <= 90;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'projectionDate':
          aValue = new Date(a.projectionDate);
          bValue = new Date(b.projectionDate);
          break;
        case 'actualProjection':
          aValue = a.actualProjection || 0;
          bValue = b.actualProjection || 0;
          break;
        case 'createdBy':
          aValue = a.createdBy || '';
          bValue = b.createdBy || '';
          break;
        case 'daysUntilRelease':
          aValue = a.daysUntilRelease || 0;
          bValue = b.daysUntilRelease || 0;
          break;
        default:
          aValue = a[sortBy] || '';
          bValue = b[sortBy] || '';
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [projections, searchTerm, filterBy, sortBy, sortOrder]);

  // Get summary statistics
  const summaryStats = useMemo(() => {
    if (!projections || projections.length === 0) return null;

    const totalProjections = projections.length;
    const latestProjection = projections.reduce((latest, current) => 
      new Date(current.projectionDate) > new Date(latest.projectionDate) ? current : latest
    );
    const avgProjection = projections.reduce((sum, p) => sum + (p.actualProjection || 0), 0) / totalProjections;
    const minProjection = Math.min(...projections.map(p => p.actualProjection || 0));
    const maxProjection = Math.max(...projections.map(p => p.actualProjection || 0));

    return {
      totalProjections,
      latestProjection,
      avgProjection,
      minProjection,
      maxProjection
    };
  }, [projections]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>Loading projections...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!projections || projections.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No Projections Available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {canManage 
            ? 'Create the first projection to get started.'
            : 'No box office projections have been created for this film yet.'
          }
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Summary Stats */}
      {summaryStats && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Projection Summary
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Projections
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {summaryStats.totalProjections}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Latest Projection
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ${(summaryStats.latestProjection.actualProjection / 1000000).toFixed(1)}M
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Average Projection
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ${(summaryStats.avgProjection / 1000000).toFixed(1)}M
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Range
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ${(summaryStats.minProjection / 1000000).toFixed(1)}M - ${(summaryStats.maxProjection / 1000000).toFixed(1)}M
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Filters and Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search projections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: '1rem' }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />

          {/* Filter by time */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Time Filter</InputLabel>
            <Select
              value={filterBy}
              label="Time Filter"
              onChange={(e) => setFilterBy(e.target.value)}
              startAdornment={<FilterList sx={{ fontSize: '1rem', mr: 1 }} />}
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="recent">Last 7 Days</MenuItem>
              <MenuItem value="month">Last 30 Days</MenuItem>
              <MenuItem value="quarter">Last 90 Days</MenuItem>
            </Select>
          </FormControl>

          {/* Sort by */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              label="Sort By"
              onChange={(e) => setSortBy(e.target.value)}
              startAdornment={<Sort sx={{ fontSize: '1rem', mr: 1 }} />}
            >
              <MenuItem value="projectionDate">Date</MenuItem>
              <MenuItem value="actualProjection">Amount</MenuItem>
              <MenuItem value="createdBy">Creator</MenuItem>
              <MenuItem value="daysUntilRelease">Days to Release</MenuItem>
            </Select>
          </FormControl>

          {/* Sort order */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Order</InputLabel>
            <Select
              value={sortOrder}
              label="Order"
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <MenuItem value="desc">Descending</MenuItem>
              <MenuItem value="asc">Ascending</MenuItem>
            </Select>
          </FormControl>

          {/* Results count */}
          <Chip
            label={`${filteredAndSortedProjections.length} projection${filteredAndSortedProjections.length !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* Projections Grid */}
      {filteredAndSortedProjections.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No Projections Match Your Filters
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filter criteria
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredAndSortedProjections.map((projection) => (
            <Grid item xs={12} sm={6} lg={4} key={projection.id}>
              <ProjectionCard
                projection={projection}
                filmId={filmId}
                filmTitle={filmTitle}
                canManage={canManage}
                onRefresh={onRefresh}
                onEditProjection={onEditProjection}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ProjectionsList;
